// Lightweight imperative toast queue. Call toast("message") from anywhere -
// no provider/context wiring needed. <ToastContainer /> (mounted once in
// App.js) subscribes and renders the actual popups.
import i18n from "../i18n/i18n.js";
import ERROR_TRANSLATIONS from "./errorTranslations.js";

let listeners = [];
let queue = [];
let nextId = 1;

function emit() {
  listeners.forEach(fn => fn(queue));
}

// Most toast() calls across the app just forward whatever plain-English string the
// backend (or a catch block) handed them. Rather than threading a translation through
// every one of those hundreds of call sites, translate here, once, at display time —
// looking the exact English string up in ERROR_TRANSLATIONS and swapping in the version
// for whatever language the user currently has the interface set to. Anything not in the
// dictionary (an unmapped string, or already-translated text) passes through unchanged.
// Exported for the (much rarer) case of an error message rendered directly on the page
// instead of via toast() — e.g. `{error && <div>{localizeMessage(error)}</div>}` — since
// those bypass the translation toast() does automatically.
export function localizeMessage(message) {
  if (typeof message !== 'string') return message;
  const lang = (i18n.language || '').split('-')[0];
  if (lang === 'en' || !lang) return message;
  const entry = ERROR_TRANSLATIONS[message.trim()];
  return entry?.[lang] || message;
}

export function toast(message, type = 'info', duration = 4500) {
  const id = nextId++;
  queue = [...queue, { id, message: localizeMessage(message), type, duration }];
  emit();
  return id;
}

toast.success = (message, duration) => toast(message, 'success', duration);
toast.error = (message, duration) => toast(message, 'error', duration);
toast.info = (message, duration) => toast(message, 'info', duration);

export function dismissToast(id) {
  queue = queue.filter(t => t.id !== id);
  emit();
}

export function subscribeToasts(fn) {
  listeners.push(fn);
  fn(queue);
  return () => { listeners = listeners.filter(l => l !== fn); };
}
